import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusPill } from "@/components/StatusPill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiClient, auth, formatMoney } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { Users, Briefcase, DollarSign, BadgeCheck, Copy, ArrowRight, Plus, Share2 } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <Card className="rounded-xl border-slate-200 shadow-sm p-6">
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="mt-2 text-3xl font-heading font-medium text-slate-900 tracking-tight">{value}</div>
      </div>
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </Card>
);

const AddPartnerDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    company_name: "",
    how_know_owners: "",
    payment_method: "ach",
    agree_terms: true,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post("/employee/partners", form);
      toast.success("Partner added");
      setOpen(false);
      setForm({ full_name: "", email: "", mobile: "", company_name: "", how_know_owners: "", payment_method: "ach", agree_terms: true });
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add partner");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={TIDS.employeeAddPartner} className="rounded-full bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-1" /> Add a partner
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Onboard a referral partner</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Full name</Label><Input value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} required /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required /></div>
            <div className="space-y-1.5"><Label>Mobile</Label><Input type="tel" value={form.mobile} onChange={(e) => upd("mobile", e.target.value)} required /></div>
            <div className="space-y-1.5"><Label>Company <span className="text-xs text-slate-500">(optional)</span></Label><Input value={form.company_name} onChange={(e) => upd("company_name", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>How they know business owners</Label>
            <Textarea rows={2} value={form.how_know_owners} onChange={(e) => upd("how_know_owners", e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>Payment method preference</Label>
            <Select value={form.payment_method} onValueChange={(v) => upd("payment_method", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ach">ACH</SelectItem>
                <SelectItem value="wire">Wire</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <Checkbox checked={form.agree_terms} onCheckedChange={(v) => upd("agree_terms", !!v)} />
            <span>I confirm the partner has agreed to program terms.</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid={TIDS.employeeAddPartnerSubmit} className="rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? "Adding…" : "Add partner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = auth.getUser();
  const recruitCode = user?.recruit_code || "";
  const recruitUrl = recruitCode ? `${window.location.origin}/apply?emp=${recruitCode}` : "";

  const load = useCallback(async () => {
    try {
      const [s, p, r] = await Promise.all([
        apiClient.get("/employee/stats"),
        apiClient.get("/employee/partners"),
        apiClient.get("/referrals"),
      ]);
      setStats(s.data);
      setPartners(p.data);
      setReferrals(r.data);
    } catch (err) {
      toast.error("Could not load dashboard");
      if (err?.response?.status === 401) navigate("/employee/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (auth.getRole() !== "employee") {
      navigate("/employee/login");
      return;
    }
    load();
  }, [navigate, load]);

  const copyLink = async () => {
    if (!recruitUrl) return;
    try {
      await navigator.clipboard.writeText(recruitUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <DashboardShell
      role="employee"
      title="Employee dashboard"
      subtitle="Your referral partners and the client applications they bring in. Click a partner to drill into their clients."
      actions={<AddPartnerDialog onCreated={load} />}
    >
      <div data-testid={TIDS.employeeDashboard} className="space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard icon={Users} label="My partners" value={stats?.total_partners ?? 0} accent="bg-blue-50 text-blue-700" />
          <StatCard icon={Briefcase} label="Referrals" value={stats?.total_referrals ?? 0} accent="bg-indigo-50 text-indigo-700" />
          <StatCard icon={BadgeCheck} label="Funded" value={stats?.funded_count ?? 0} accent="bg-emerald-50 text-emerald-700" />
          <StatCard icon={DollarSign} label="Commission unpaid" value={formatMoney(stats?.total_commission_unpaid ?? 0)} accent="bg-amber-50 text-amber-700" />
        </div>

        {/* Recruit link card */}
        <Card data-testid={TIDS.employeeRecruitLinkCard} className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
          <div className="grid md:grid-cols-3">
            <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white p-6 md:p-8">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur"><Share2 className="h-5 w-5" /></span>
              <h2 className="mt-5 text-2xl font-heading font-medium tracking-tight leading-tight">
                Your recruit link.<br /><span className="text-white/80">Share with prospective partners.</span>
              </h2>
            </div>
            <div className="md:col-span-2 p-6 md:p-8 space-y-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Auto-attribute partners who sign up via this link</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <code className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 font-mono overflow-x-auto whitespace-nowrap">
                  {recruitUrl || "Loading…"}
                </code>
                <Button onClick={copyLink} data-testid={TIDS.employeeRecruitLinkCopy} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white shrink-0">
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Anyone who signs up through this link is automatically attributed to you. You can also onboard a partner directly using the &ldquo;Add a partner&rdquo; button.
              </p>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="partners">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1">
            <TabsTrigger value="partners" className="rounded-full px-5">My partners</TabsTrigger>
            <TabsTrigger value="referrals" className="rounded-full px-5">All clients</TabsTrigger>
          </TabsList>

          <TabsContent value="referrals" className="mt-5">
            <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-10 text-center text-slate-500">Loading…</div>
              ) : referrals.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No referrals yet from your partners.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Business</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Funding</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <div className="font-medium text-slate-900">{r.business_name}</div>
                          <div className="text-xs text-slate-500">{r.business_owner_name}</div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {r.partner_snapshot?.full_name}
                          <div className="text-xs text-slate-500">{r.partner_snapshot?.email}</div>
                        </TableCell>
                        <TableCell><StatusPill status={r.status} /></TableCell>
                        <TableCell className="text-right text-slate-700">
                          {r.funding_amount ? formatMoney(r.funding_amount) : <span className="text-slate-400">{formatMoney(r.funding_amount_needed)}</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.commission_amount
                            ? <span className={r.commission_paid ? "text-emerald-700 font-medium" : "text-slate-900 font-medium"}>{formatMoney(r.commission_amount)}</span>
                            : <span className="text-slate-500">{formatMoney(r.estimated_commission)}</span>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <Link to={`/employee/referrals/${r.id}`}>
                            <Button variant="ghost" size="sm" className="hover:text-blue-700">Manage <ArrowRight className="ml-1 h-4 w-4" /></Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="partners" className="mt-5">
            <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-medium text-slate-900">Partners I recruited</h2>
                <span className="text-sm text-slate-500">{partners.length} total</span>
              </div>
              {partners.length === 0 ? (
                <div className="p-10 text-center text-slate-500">
                  No partners yet. Use <span className="font-medium">Add a partner</span> above, or share your recruit link.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead className="text-right">Clients</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partners.map((p) => (
                      <TableRow
                        key={p.id}
                        data-testid={TIDS.empPartnerRowDrill}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/employee/partners/${p.id}`)}
                      >
                        <TableCell className="font-medium text-slate-900">{p.full_name}</TableCell>
                        <TableCell className="text-slate-700">{p.email}</TableCell>
                        <TableCell className="text-slate-700">{p.mobile}</TableCell>
                        <TableCell className="text-slate-700">{p.company_name || "—"}</TableCell>
                        <TableCell className="text-right font-medium">{p.referral_count}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost" size="sm"
                            onClick={(ev) => { ev.stopPropagation(); navigate(`/employee/partners/${p.id}`); }}
                            className="hover:text-blue-700"
                          >
                            View clients <ArrowRight className="ml-1 h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardShell>
  );
}
